/*
 * Shared lead submit — payload assembly, Turnstile token and POST for EVERY
 * storefront form that creates an inquiry.
 *
 * Why this exists: there are several inquiry forms and each owns a genuinely
 * different UI (an inline form, a <dialog> on the PDP, a package picker, the setup
 * builder). What is identical is everything AFTER the user hits submit — the same
 * fields, the same JSON, the same endpoint. Keeping four copies of that meant a new
 * field reached one form and silently skipped the rest, which is exactly how the
 * Meta _fbp/_fbc identifiers ended up missing from three of four forms.
 *
 * Loaded site-wide from layout/theme.liquid (defer, before any section script), so
 * every form can rely on window.bqBuildLeadPayload being there.
 *
 * ── THE TURNSTILE CONTRACT ────────────────────────────────────────────────────
 * A submit button must NEVER be disabled while waiting for Turnstile. The widget's
 * verify callback is the one signal that has repeatedly failed to arrive (registered
 * too late, fired before the handler existed, token stored somewhere we didn't look),
 * and a button gated on it stays dead forever under a green checkmark — the visitor
 * clicks and nothing happens. Instead:
 *   1. leave the button enabled at all times;
 *   2. read the token at submit time with bqLeadToken(form);
 *   3. if it is missing, show [data-turnstile-hint] and return — a visible reason,
 *      not silence. Clicking again once the widget catches up goes through.
 * The token is still mandatory: the server verifies it (POST /leads → siteverify).
 * See theme/docs/components/lead-forms.md.
 */
(function () {
  var CONTACT_FIELDS = ['name', 'email', 'phone', 'company', 'country'];

  /*
   * ── EMAIL: STRICTER THAN THE BROWSER ──────────────────────────────────────────
   * <input type="email"> accepts "jane@company" — the HTML spec allows a bare host
   * on purpose, for intranet addresses. Our API validates properly and answers 422,
   * which the form could only report as "something went wrong on our side": a lie
   * that blames us for a typo and gives the visitor nothing to fix. So require a
   * dotted domain here, where the browser can put the message on the field itself.
   * Delegated from the document, so it covers every form including ones added later.
   */
  var EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

  function emailMessage() {
    var host = document.querySelector('[data-lead-strings]');
    return (host && host.dataset.emailInvalid) || 'Enter a full email address, like name@company.com.';
  }

  function checkEmail(el) {
    if (!el || el.tagName !== 'INPUT' || el.type !== 'email' || !el.setCustomValidity) return;
    var value = String(el.value || '').trim();
    // An empty field is the `required` attribute's business, not ours.
    el.setCustomValidity(!value || EMAIL_RE.test(value) ? '' : emailMessage());
  }

  document.addEventListener('input', function (e) { checkEmail(e.target); });
  // `blur` does not bubble; capture catches it anyway, so someone who pastes an
  // address and tabs on still gets told before they reach the button.
  document.addEventListener('blur', function (e) { checkEmail(e.target); }, true);

  /*
   * The Turnstile token, asked for in the order of decreasing reliability: the widget
   * API answers wherever Cloudflare chose to put the token, the hidden input is the
   * fallback for the window before api.js has run. Never trust a JS flag set from a
   * callback — that is the thing that goes missing.
   */
  window.bqLeadToken = function (form) {
    if (!form) return '';
    try {
      var widget = form.querySelector('.cf-turnstile');
      if (widget && window.turnstile && window.turnstile.getResponse) {
        var token = window.turnstile.getResponse(widget);
        if (token) return token;
      }
    } catch (e) { /* widget not rendered yet — fall through to the input */ }
    var input = form.querySelector('input[name="cf-turnstile-response"]');
    return input ? input.value || '' : '';
  };

  /*
   * First-touch UTM captured site-wide by assets/utm.js, with the current URL as a
   * fallback for the case where utm.js never ran — then at least a visitor who lands
   * on a campaign URL and submits straight away is still attributed.
   */
  function readUtm() {
    try {
      if (window.bqGetUtm) {
        var stored = window.bqGetUtm();
        if (stored && Object.keys(stored).length) return stored;
      }
    } catch (e) { /* ignore */ }
    var utm = {};
    try {
      new URLSearchParams(window.location.search).forEach(function (v, k) {
        if (k.lastIndexOf('utm_', 0) === 0 && v) utm[k] = v;
      });
    } catch (e) { /* no URLSearchParams */ }
    return utm;
  }

  /* Show/hide the "finish the check" notice that replaces a disabled button. */
  window.bqLeadHint = function (form, show) {
    var hint = form && form.querySelector('[data-turnstile-hint]');
    if (!hint) return;
    hint.hidden = !show;
    if (show && hint.scrollIntoView) hint.scrollIntoView({ block: 'nearest' });
  };

  /*
   * Assemble the POST /leads body (schemas/leads.py::LeadCreate).
   *
   * form          — the <form> holding the contact inputs
   * source        — LeadSource value ('contact', 'product_quote', 'set_quote', …)
   * extraDetails  — optional object merged into `details` for whatever this form
   *                 collects that plain [data-lead-detail] inputs cannot express
   *                 (the builder's spec, the chosen package and its total)
   *
   * ⚠️ A new field that every form should send belongs HERE and nowhere else.
   */
  window.bqBuildLeadPayload = function (form, source, extraDetails) {
    function val(name) {
      var el = form.elements[name];
      return el && el.value != null ? String(el.value).trim() : '';
    }

    var payload = { source: source, source_url: window.location.href, details: {} };

    CONTACT_FIELDS.forEach(function (name) {
      var v = val(name);
      if (v) payload[name] = v;
    });
    var message = val('message');
    if (message) payload.message = message;

    // Form-specific inputs marked [data-lead-detail] ride in `details`, keyed by name.
    // Checkboxes and radios count only when checked — their `value` is set either way —
    // and several ticked boxes sharing one name join into one comma-separated string
    // instead of the last one overwriting the rest.
    var marked = form.querySelectorAll('[data-lead-detail]');
    for (var i = 0; i < marked.length; i++) {
      var el = marked[i];
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue;
      if (el.name && el.value && String(el.value).trim()) {
        var v = String(el.value).trim();
        var prev = payload.details[el.name];
        payload.details[el.name] = el.type === 'checkbox' && prev ? prev + ', ' + v : v;
      }
    }
    if (extraDetails) {
      for (var key in extraDetails) {
        if (Object.prototype.hasOwnProperty.call(extraDetails, key)) {
          payload.details[key] = extraDetails[key];
        }
      }
    }

    // Attribution. All of it is captured site-wide by assets/utm.js / referral.js and
    // read here, because the entry-URL parameters are long gone by the time someone
    // fills in a form several pages later.
    var ref = (window.bqGetRef && window.bqGetRef()) || val('referral_code');
    if (ref) payload.referral_code = ref;
    payload.utm = readUtm();
    var landing = (window.bqGetLanding && window.bqGetLanding()) || null;
    if (landing) payload.landing = landing;
    // The external site that sent them, when the link carried no utm — the only way an
    // AI-answer or forum referral is visible at all.
    var referrer = (window.bqGetReferrer && window.bqGetReferrer()) || null;
    if (referrer) payload.referrer = referrer;
    // Meta match keys for the server-side Lead event (ops/plans/meta-conversions-api.md).
    var fbp = (window.bqGetFbp && window.bqGetFbp()) || null;
    if (fbp) payload.fbp = fbp;
    var fbc = (window.bqGetFbc && window.bqGetFbc()) || null;
    if (fbc) payload.fbc = fbc;
    // GA4 cookies for the server-side generate_lead (ops/plans/ga4-lead-key-events.md).
    var ga = (window.bqGetGa && window.bqGetGa()) || {};
    if (ga.client) payload.ga_cookie = ga.client;
    if (ga.session) payload.ga_session_cookie = ga.session;

    payload.turnstile_token = window.bqLeadToken(form);
    payload.hp_field = val('hp_field');

    return payload;
  };

  /*
   * POST it. Resolves true on 2xx and false on anything else — a real network failure
   * (offline/DNS) and a rejected submit (rate limit, failed Turnstile, validation) are
   * the same thing to a visitor: it did not go through, say so.
   */
  window.bqSubmitLead = function (url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(
      function (res) { return !!(res && res.ok); },
      function () { return false; }
    );
  };
})();

